/* eslint-disable sonarjs/no-duplicate-string */
import React from 'react';
import { getEnvironment } from '@minecraft.environment';
import { MediaTypeCodeEnum } from '@minecraft.graphql-operations';
import { formatBytes } from '@minecraft.utils';
import { captureExceptionEvent } from '../Sentry/Sentry';
import { TranslateFn } from '../I18N/types';
import { CLIENT_ALLOWED_FILE_TYPES, MAX_FILE_SIZE_FOR_MEDIA_TYPE, MIN_IMAGE_DIMENSIONS } from './constants';
import { FileTypeWithMeta, UploadWidgetOptions, UploadWidgetValidationOptions } from './types';

const ONE_KB = 1024;
// Determine the length of the base64 header for a PNG image
const BASE_64_HEADER_LENGTH = 'data:image/png;base64,'.length;

export const getAllowedFileTypeForMedia = (mediaType: MediaTypeCodeEnum, isForDnD?: boolean) => {
  return CLIENT_ALLOWED_FILE_TYPES[mediaType].map((fileType) => (isForDnD ? fileType : `.${fileType}`)).join(',');
};

export const getEscapedFilename = (file: File) => {
  // removes the file extension and replaces any periods with spaces
  const pieces = file.name.split('.');
  pieces.pop();
  return pieces.join(' ');
};

export const getFileExtension = (fileName: File['type']) => {
  const fileNameSplitted = fileName.split('.');

  return fileNameSplitted[fileNameSplitted.length - 1].toUpperCase();
};

export const isAllowedFileTypeToDnD = (file: File, currentMediaType: MediaTypeCodeEnum) => {
  const fileName = file.name.toLowerCase();
  const acceptedExtensions = getAllowedFileTypeForMedia(currentMediaType, true);
  const fileExtension = fileName.split('.').pop().toLowerCase();

  return acceptedExtensions.includes(fileExtension);
};

export const handleImage = (file: File, t: TranslateFn): Promise<FileTypeWithMeta> => {
  return new Promise((resolve, reject) => {
    const formattedFileSize = formatBytes(file.size);
    const formattedMaxAllowed = formatBytes(MAX_FILE_SIZE_FOR_MEDIA_TYPE[MediaTypeCodeEnum.Photo]);

    if (!isAllowedFileTypeToDnD(file, MediaTypeCodeEnum.Photo)) {
      reject(new Error(t('common:cloudinaryUploader.uploader.errors.allowed_formats')));

      return;
    }

    if (file.size > MAX_FILE_SIZE_FOR_MEDIA_TYPE[MediaTypeCodeEnum.Photo]) {
      reject(
        new Error(
          t('common:cloudinaryUploader.uploader.errors.file_too_large', {
            size: formattedFileSize,
            allowed: formattedMaxAllowed,
          })
        )
      );

      return;
    }

    const imgSrc = URL.createObjectURL(file);
    const img = new Image();
    img.src = imgSrc;

    img.onload = () => {
      const { width, height } = img;

      if (width < MIN_IMAGE_DIMENSIONS.WIDTH || height < MIN_IMAGE_DIMENSIONS.HEIGHT) {
        reject(
          new Error(
            t('common:cloudinaryUploader.uploader.errors.min_dimensions_validation', {
              minWidth: MIN_IMAGE_DIMENSIONS.WIDTH,
              minHeight: MIN_IMAGE_DIMENSIONS.HEIGHT,
              width,
              height,
            })
          )
        );

        return;
      }

      resolve({
        fileName: file.name,
        fileSize: formattedFileSize,
        fileType: getFileExtension(file.name),
        originalFile: file,
        fileMediaType: MediaTypeCodeEnum.Photo,
        id: file.name,
        fileThumbnailSrc: imgSrc,
        width,
        height,
      });
    };
    img.onerror = () => reject(new Error(t('common:media.utilization.couldNotLoad', { name: file.name })));
  });
};

export const checkIf3GPEncoded = (file: File): Promise<boolean> | boolean => {
  if (!file) {
    return false;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event: ProgressEvent<FileReader> | null) => {
      let is3GP = false;
      if (event?.target?.result instanceof ArrayBuffer) {
        const data = new Uint8Array(event.target.result);

        // Look for a specific 3GP file pattern
        const pattern = new Uint8Array([0x66, 0x74, 0x79, 0x70, 0x33, 0x67]); // ASCII for "ftyp3g"

        // Search for the pattern in the file's header (typically within the first few kilobytes)
        for (let i = 0; i < Math.min(ONE_KB, data.length - pattern.length); i += 1) {
          let match = true;
          for (let j = 0; j < pattern.length; j += 1) {
            if (data[i + j] !== pattern[j]) {
              match = false;
              break;
            }
          }
          if (match) {
            is3GP = true;
            break;
          }
        }
      }
      resolve(is3GP);
    };

    reader.onerror = (e) => {
      reject(e);
    };

    // Read the first few kilobytes of the file to check for the pattern
    const blob: Blob = file.slice(0, ONE_KB);
    reader.readAsArrayBuffer(blob);
  });
};

export const handleVideo = (file: File, t: TranslateFn): Promise<FileTypeWithMeta> => {
  const formattedFileSize = formatBytes(file.size);
  const formattedMaxAllowed = formatBytes(MAX_FILE_SIZE_FOR_MEDIA_TYPE[MediaTypeCodeEnum.Video]);

  return new Promise((resolve, reject) => {
    if (!isAllowedFileTypeToDnD(file, MediaTypeCodeEnum.Video)) {
      reject(new Error(t('common:cloudinaryUploader.uploader.errors.allowed_formats')));

      return;
    }

    if (file.size > MAX_FILE_SIZE_FOR_MEDIA_TYPE[MediaTypeCodeEnum.Video]) {
      reject(
        new Error(
          t('common:cloudinaryUploader.uploader.errors.file_too_large', {
            size: formattedFileSize,
            allowed: formattedMaxAllowed,
          })
        )
      );

      return;
    }

    const fileReader = new FileReader();
    fileReader.onload = async () => {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      let resolved = false;
      let timeOutValue: ReturnType<typeof setTimeout>;

      const resolveFile = (thumbnail?: string) => {
        if (!resolved) {
          resolved = true;
          resolve({
            fileName: file.name,
            id: file.name,
            fileSize: formattedFileSize,
            fileType: getFileExtension(file.name),
            fileThumbnailSrc: thumbnail,
            fileMediaType: MediaTypeCodeEnum.Video,
            videoFileSrc: url,
            originalFile: file,
          });
        }
      };

      const snapImage = () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        const image = canvas.toDataURL();

        return image.length <= BASE_64_HEADER_LENGTH ? null : image;
      };

      const handleTimeupdate = () => {
        const thumbnail = snapImage();
        if (thumbnail) {
          video.removeEventListener('timeupdate', handleTimeupdate);
          video.pause();
          clearTimeout(timeOutValue);
          resolveFile(thumbnail);
        }
      };

      video.addEventListener('loadeddata', () => {
        const thumbnail = snapImage();
        if (thumbnail) {
          video.removeEventListener('timeupdate', handleTimeupdate);
          clearTimeout(timeOutValue);
          resolveFile(thumbnail);
        }
      });

      video.addEventListener('timeupdate', handleTimeupdate);
      video.onerror = (e) => {
        console.error('video element error', e);
        captureExceptionEvent(e, { location: 'MediaUploader - handleVideo - video.onerror' });
        reject(new Error(t('common:media.utilization.couldNotLoad', { name: file.name })));
      };
      video.preload = 'metadata';
      video.src = url;
      // Load video in Safari / IE11
      video.muted = true;
      video.playsInline = true;
      await video.play();

      timeOutValue = setTimeout(() => {
        // prevent the video from continuing to play & firing timeupdate events since we'll no longer be listening
        video.pause();
        video.removeEventListener('timeupdate', handleTimeupdate);
        // if the video thumbnail does not resolve in 1.5 seconds, resolve with no thumbnail
        // for this asset, but continue and attempt upload anyway
        resolveFile(null);
      }, 1500);
    };
    fileReader.onerror = (e) => {
      console.error({ cbErr: e, readerErr: fileReader.error });
      captureExceptionEvent(fileReader.error, { location: 'MediaUploader - handleVideo - fileReader.onerror' });
      reject(new Error(t('common:media.utilization.couldNotLoad', { name: file.name })));
    };
    fileReader.readAsArrayBuffer(file);
  });
};

export const handleAudio = (file: File, t: TranslateFn): Promise<FileTypeWithMeta> => {
  return new Promise((resolve, reject) => {
    const formattedFileSize = formatBytes(file.size);
    const formattedMaxAllowed = formatBytes(MAX_FILE_SIZE_FOR_MEDIA_TYPE[MediaTypeCodeEnum.Audio]);

    if (!isAllowedFileTypeToDnD(file, MediaTypeCodeEnum.Audio)) {
      reject(new Error(t('common:cloudinaryUploader.uploader.errors.allowed_formats')));

      return;
    }

    if (file.size > MAX_FILE_SIZE_FOR_MEDIA_TYPE[MediaTypeCodeEnum.Audio]) {
      reject(
        new Error(
          t('common:cloudinaryUploader.uploader.errors.file_too_large', {
            size: formattedFileSize,
            allowed: formattedMaxAllowed,
          })
        )
      );

      return;
    }

    resolve({
      fileName: file.name,
      id: file.name,
      fileSize: formattedFileSize,
      fileType: getFileExtension(file.name),
      fileMediaType: MediaTypeCodeEnum.Audio,
      fileThumbnailSrc: URL.createObjectURL(file),
      originalFile: file,
    });
  });
};

export const handleDoc = (file: File, t: TranslateFn): Promise<FileTypeWithMeta> => {
  return new Promise((resolve, reject) => {
    const formattedFileSize = formatBytes(file.size);
    const formattedMaxAllowed = formatBytes(MAX_FILE_SIZE_FOR_MEDIA_TYPE[MediaTypeCodeEnum.Document]);

    if (!isAllowedFileTypeToDnD(file, MediaTypeCodeEnum.Document)) {
      reject(new Error(t('common:cloudinaryUploader.uploader.errors.allowed_formats')));

      return;
    }

    if (file.size > MAX_FILE_SIZE_FOR_MEDIA_TYPE[MediaTypeCodeEnum.Document]) {
      reject(
        new Error(
          t('common:cloudinaryUploader.uploader.errors.file_too_large', {
            size: formattedFileSize,
            allowed: formattedMaxAllowed,
          })
        )
      );

      return;
    }

    resolve({
      fileName: file.name,
      id: file.name,
      fileSize: formattedFileSize,
      fileType: getFileExtension(file.name),
      fileMediaType: MediaTypeCodeEnum.Document,
      fileThumbnailSrc: URL.createObjectURL(file),
      originalFile: file,
    });
  });
};

type HandleFilesUploadParams = {
  eventFiles: File | File[];
  t: TranslateFn;
  maxFiles: number;
  currentMediaType: MediaTypeCodeEnum;
};

export const handleFilesUpload = async ({ eventFiles, t, maxFiles, currentMediaType }: HandleFilesUploadParams) => {
  const result: { error?: string; filesToPreview: FileTypeWithMeta[] } = {
    error: null,
    filesToPreview: [],
  };

  try {
    if (!eventFiles) {
      throw new Error('No Files Selected');
    }

    const filesToPreview = Array.isArray(eventFiles) ? eventFiles : [eventFiles];

    if (maxFiles != null && filesToPreview.length > maxFiles) {
      throw new Error(t('common:cloudinaryUploader.uploader.errors.max_number_of_files'));
    }

    const filesToPreviewData: FileTypeWithMeta[] = await Promise.all(
      [...filesToPreview].map((file: File): Promise<FileTypeWithMeta> => {
        if (currentMediaType === MediaTypeCodeEnum.Photo) {
          return handleImage(file, t);
        }

        if (currentMediaType === MediaTypeCodeEnum.Video) {
          return handleVideo(file, t);
        }

        if (currentMediaType === MediaTypeCodeEnum.Audio) {
          return handleAudio(file, t);
        }

        if (currentMediaType === MediaTypeCodeEnum.Document) {
          return handleDoc(file, t);
        }

        // if we are not passed a valid media type to operate on, reject the upload
        return Promise.reject(new Error(t('common:media.utilization.corruptedFile')));
      })
    );

    result.filesToPreview = filesToPreviewData;
    return result;
  } catch (error) {
    result.error = error?.message || t('common:cloudinaryUploader.uploader.errors.allowed_formats');
    return result;
  }
};

export const handleDragEnter: React.DragEventHandler<HTMLDivElement> = (event) => {
  event.stopPropagation();
  event.preventDefault();
};

export const handleDragOver: React.DragEventHandler<HTMLDivElement> = (event) => {
  event.stopPropagation();
  event.preventDefault();
};

export const getWidgetUploadPreset = (mediaType?: MediaTypeCodeEnum): UploadWidgetOptions['uploadPreset'] => {
  const environment = getEnvironment();
  const uploadPresetVideo = environment?.UPLOAD_PRESET_VIDEO;
  const uploadPresetAudio = environment?.UPLOAD_PRESET_AUDIO;
  const uploadPresetDefault = environment?.UPLOAD_PRESET_DEFAULT;

  switch (mediaType) {
    // Doesn't contain any transformations, just points to specific folder (environment wise) e.g. qa or stg
    case MediaTypeCodeEnum.Photo:
    case MediaTypeCodeEnum.Document:
      return uploadPresetDefault;
    // Points to specific folder (environment wise) e.g. qa or stg
    // And has an eager video transformations to .mp4
    case MediaTypeCodeEnum.Video:
      return uploadPresetVideo;
    // Points to specific folder (environment wise) e.g. qa or stg
    // And has an eager video transformations to .mp3
    case MediaTypeCodeEnum.Audio:
      return uploadPresetAudio;
    default:
      return null;
  }
};

export const buildWidgetValidationRules = (mediaType?: MediaTypeCodeEnum): UploadWidgetValidationOptions => {
  switch (mediaType) {
    case MediaTypeCodeEnum.Photo:
      return {
        maxFileSize: MAX_FILE_SIZE_FOR_MEDIA_TYPE[MediaTypeCodeEnum.Photo],
        minImageWidth: MIN_IMAGE_DIMENSIONS.WIDTH,
        minImageHeight: MIN_IMAGE_DIMENSIONS.HEIGHT,
        clientAllowedFormats: CLIENT_ALLOWED_FILE_TYPES[MediaTypeCodeEnum.Photo],
      };
    case MediaTypeCodeEnum.Audio:
      return {
        maxFileSize: MAX_FILE_SIZE_FOR_MEDIA_TYPE[MediaTypeCodeEnum.Audio],
        // formats that supported for upload and delivery by cloudinary
        // https://cloudinary.com/documentation/audio_transformations#supported_audio_formats
        clientAllowedFormats: CLIENT_ALLOWED_FILE_TYPES[MediaTypeCodeEnum.Audio],
      };
    case MediaTypeCodeEnum.Video:
      return {
        maxFileSize: MAX_FILE_SIZE_FOR_MEDIA_TYPE[MediaTypeCodeEnum.Video],
        // formats that supported for upload and delivery by cloudinary
        // https://cloudinary.com/documentation/video_manipulation_and_delivery#supported_video_formats
        clientAllowedFormats: CLIENT_ALLOWED_FILE_TYPES[MediaTypeCodeEnum.Video],
      };
    case MediaTypeCodeEnum.Document:
      return {
        maxFileSize: MAX_FILE_SIZE_FOR_MEDIA_TYPE[MediaTypeCodeEnum.Document],
        clientAllowedFormats: CLIENT_ALLOWED_FILE_TYPES[MediaTypeCodeEnum.Document],
      };
    default:
      return {
        maxFileSize: ONE_KB ** 3,
      };
  }
};

// these are type-guard functions used to narrow down the type of the event
// in the widget event handler callback
export const WIDGET_EVENT_CHECKS = {
  abort: (e: cloudinary.Event<cloudinary.CloudinaryEvent>): e is cloudinary.Event<'abort'> => e.event === 'abort',
  'batch-cancelled': (e: cloudinary.Event<cloudinary.CloudinaryEvent>): e is cloudinary.Event<'batch-cancelled'> =>
    e.event === 'batch-cancelled',
  close: (e: cloudinary.Event<cloudinary.CloudinaryEvent>): e is cloudinary.Event<'close'> => e.event === 'close',
  'display-changed': (e: cloudinary.Event<cloudinary.CloudinaryEvent>): e is cloudinary.Event<'display-changed'> =>
    e.event === 'display-changed',
  publicid: (e: cloudinary.Event<cloudinary.CloudinaryEvent>): e is cloudinary.Event<'publicid'> =>
    e.event === 'publicid',
  'queues-end': (e: cloudinary.Event<cloudinary.CloudinaryEvent>): e is cloudinary.Event<'queues-end'> =>
    e.event === 'queues-end',
  'queues-start': (e: cloudinary.Event<cloudinary.CloudinaryEvent>): e is cloudinary.Event<'queues-start'> =>
    e.event === 'queues-start',
  retry: (e: cloudinary.Event<cloudinary.CloudinaryEvent>): e is cloudinary.Event<'retry'> => e.event === 'retry',
  'show-completed': (e: cloudinary.Event<cloudinary.CloudinaryEvent>): e is cloudinary.Event<'show-completed'> =>
    e.event === 'show-completed',
  'source-changed': (e: cloudinary.Event<cloudinary.CloudinaryEvent>): e is cloudinary.Event<'source-changed'> =>
    e.event === 'source-changed',
  success: (e: cloudinary.Event<cloudinary.CloudinaryEvent>): e is cloudinary.Event<'success'> => e.event === 'success',
  tags: (e: cloudinary.Event<cloudinary.CloudinaryEvent>): e is cloudinary.Event<'tags'> => e.event === 'tags',
  'upload-added': (e: cloudinary.Event<cloudinary.CloudinaryEvent>): e is cloudinary.Event<'upload-added'> =>
    e.event === 'upload-added',
} as const;
